import { apiService, ApiResponse } from './api';
import { 
  Booking, 
  BookingFilters, 
  BookingListParams,
  BookingListResponse,
  AdminUpdateBookingDto, 
  UpdateBookingStatusDto, 
  ProcessPaymentDto,
  BookingSummary,
  BookingStatus,
  PaymentStatus
} from '../types/booking';

export class BookingService {
  private basePath = '/api/bookings';

  async getAllBookings(filters?: BookingFilters): Promise<ApiResponse<Booking[]>> {
    console.log('📋 BookingService: getAllBookings called with filters:', filters);
    const queryParams = new URLSearchParams();
    
    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          if (value instanceof Date) {
            queryParams.append(key, value.toISOString());
          } else {
            queryParams.append(key, String(value));
          }
        }
      });
    }
    
    const queryString = queryParams.toString();
    const endpoint = queryString ? `${this.basePath}?${queryString}` : this.basePath;
    console.log('🔗 BookingService: Calling endpoint:', endpoint);
    
    try {
      const response = await apiService.get<Booking[]>(endpoint);
      console.log('✅ BookingService: getAllBookings response:', response);
      console.log('📋 BookingService: Bookings count:', response.data?.length || 0);
      return response;
    } catch (error) {
      console.error('❌ BookingService: getAllBookings error:', error);
      throw error;
    }
  }

  async getBookingsPage(
    params?: BookingListParams,
  ): Promise<ApiResponse<BookingListResponse>> {
    const queryParams = new URLSearchParams();

    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          if (value instanceof Date) {
            queryParams.append(key, value.toISOString());
          } else {
            queryParams.append(key, String(value));
          }
        }
      });
    }

    const queryString = queryParams.toString();
    const endpoint = queryString
      ? `${this.basePath}/list?${queryString}`
      : `${this.basePath}/list`;

    return apiService.get<BookingListResponse>(endpoint);
  }

  async getBookingById(id: string): Promise<ApiResponse<Booking>> {
    return apiService.get<Booking>(`${this.basePath}/booking/${id}`);
  }

  async updateBooking(id: string, bookingData: AdminUpdateBookingDto): Promise<ApiResponse<Booking>> {
    console.log('📝 BookingService.updateBooking called');
    console.log('   Booking ID:', id);
    console.log('   Update data:', bookingData);
    
    try {
      const response = await apiService.put<Booking>(`${this.basePath}/admin/${id}`, bookingData);
      console.log('📡 BookingService.updateBooking response:', response);
      return response;
    } catch (error) {
      console.error('❌ BookingService.updateBooking error:', error);
      throw error;
    }
  }

  async updateBookingStatus(id: string, statusData: UpdateBookingStatusDto): Promise<ApiResponse<Booking>> {
    return apiService.patch<Booking>(`${this.basePath}/${id}/status`, statusData);
  }

  async processPayment(id: string, paymentData: ProcessPaymentDto): Promise<ApiResponse<Booking>> {
    return apiService.post<Booking>(`${this.basePath}/${id}/payment`, paymentData);
  }

  async processRefund(id: string, refundAmount: number, reason?: string): Promise<ApiResponse<Booking>> {
    return apiService.post<Booking>(`${this.basePath}/${id}/refund`, { 
      refundAmount, 
      reason 
    });
  }

  async approveBooking(id: string, approvedBy: string): Promise<ApiResponse<Booking>> {
    return apiService.post<Booking>(`${this.basePath}/${id}/approve`, { approvedBy });
  }

  async rejectBooking(id: string, rejectionReason: string): Promise<ApiResponse<Booking>> {
    return apiService.post<Booking>(`${this.basePath}/${id}/reject`, { rejectionReason });
  }

  async addBookingImage(
    id: string, 
    imageData: {
      url: string;
      type: 'pickup' | 'return' | 'damage' | 'other';
      description?: string;
      uploadedBy?: string;
    }
  ): Promise<ApiResponse<Booking>> {
    return apiService.post<Booking>(`${this.basePath}/${id}/images`, imageData);
  }

  async addBookingDocument(
    id: string, 
    documentData: {
      name: string;
      url: string;
      type: 'contract' | 'license' | 'insurance' | 'receipt' | 'other';
    }
  ): Promise<ApiResponse<Booking>> {
    return apiService.post<Booking>(`${this.basePath}/${id}/documents`, documentData);
  }

  async checkBookingConflict(
    carId: string,
    pickUpDate: Date,
    returnDate: Date,
    excludeBookingId?: string
  ): Promise<ApiResponse<boolean>> {
    const params = new URLSearchParams({
      carId,
      pickUpDate: pickUpDate.toISOString(),
      returnDate: returnDate.toISOString()
    });
    
    if (excludeBookingId) {
      params.append('excludeBookingId', excludeBookingId);
    }
    
    return apiService.get<boolean>(`${this.basePath}/check-conflict?${params.toString()}`);
  }

  async resolveBookingConflict(
    id: string, 
    resolution: {
      resolvedBy: string;
      resolution: string;
    }
  ): Promise<ApiResponse<Booking>> {
    return apiService.post<Booking>(`${this.basePath}/${id}/resolve-conflict`, resolution);
  }

  async getConflictingBookings(
    carId: string,
    pickUpDate: Date,
    returnDate: Date,
    excludeBookingId?: string
  ): Promise<ApiResponse<Booking[]>> {
    const params = new URLSearchParams({
      carId,
      pickUpDate: pickUpDate.toISOString(),
      returnDate: returnDate.toISOString()
    });
    
    if (excludeBookingId) {
      params.append('excludeBookingId', excludeBookingId);
    }
    
    return apiService.get<Booking[]>(`${this.basePath}/conflicts?${params.toString()}`);
  }

  async getBookingSummary(): Promise<ApiResponse<BookingSummary>> {
    return apiService.get<BookingSummary>(`${this.basePath}/summary`);
  }

  async getBookingsByDateRange(startDate: Date, endDate: Date): Promise<ApiResponse<Booking[]>> {
    const params = new URLSearchParams({
      startDate: startDate.toISOString(),
      endDate: endDate.toISOString()
    });
    
    return apiService.get<Booking[]>(`${this.basePath}/date-range?${params.toString()}`);
  }

  async getUpcomingBookings(days: number = 7): Promise<ApiResponse<Booking[]>> {
    return apiService.get<Booking[]>(`${this.basePath}/upcoming?days=${days}`);
  }

  async getOverdueReturns(): Promise<ApiResponse<Booking[]>> {
    return apiService.get<Booking[]>(`${this.basePath}/overdue-returns`);
  }

  async deleteBooking(id: string): Promise<ApiResponse<void>> {
    return apiService.delete<void>(`${this.basePath}/${id}`);
  }

  async createManualBooking(bookingData: any): Promise<ApiResponse<Booking>> {
    return apiService.post<Booking>(`${this.basePath}/manual`, bookingData);
  }

  // Utility methods for status and payment status display
  getStatusColor(status: BookingStatus): string {
    switch (status) {
      case BookingStatus.PENDING: return 'bg-yellow-100 text-yellow-800';
      case BookingStatus.CONFIRMED: return 'bg-blue-100 text-blue-800';
      case BookingStatus.IN_PROGRESS: return 'bg-green-100 text-green-800';
      case BookingStatus.COMPLETED: return 'bg-gray-100 text-gray-800';
      case BookingStatus.CANCELLED: return 'bg-red-100 text-red-800';
      case BookingStatus.NO_SHOW: return 'bg-orange-100 text-orange-800';
      case BookingStatus.MODIFIED: return 'bg-purple-100 text-purple-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  }

  getPaymentStatusColor(status: PaymentStatus): string {
    switch (status) {
      case PaymentStatus.PENDING: return 'bg-yellow-100 text-yellow-800';
      case PaymentStatus.PAID: return 'bg-green-100 text-green-800';
      case PaymentStatus.PARTIAL: return 'bg-blue-100 text-blue-800';
      case PaymentStatus.REFUNDED: return 'bg-gray-100 text-gray-800';
      case PaymentStatus.FAILED: return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  }

  formatStatus(status: string): string {
    return status.split('-').map(word => 
      word.charAt(0).toUpperCase() + word.slice(1)
    ).join(' ');
  }

  calculateBookingDuration(pickUpDate: Date, returnDate: Date): number {
    const diffTime = Math.abs(returnDate.getTime() - pickUpDate.getTime());
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  }

  isBookingOverdue(booking: Booking): boolean {
    if (booking.status !== BookingStatus.IN_PROGRESS) return false;
    return new Date() > new Date(booking.returnDate);
  }

  isBookingUpcoming(booking: Booking, days: number = 7): boolean {
    if (booking.status !== BookingStatus.CONFIRMED) return false;
    const pickUpDate = new Date(booking.pickUpDate);
    const today = new Date();
    const daysUntilPickup = Math.ceil((pickUpDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
    return daysUntilPickup <= days && daysUntilPickup >= 0;
  }
}

export const bookingService = new BookingService();
